import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Zap, Building2, Clock, AlertTriangle } from 'lucide-react';

interface ProviderResult {
  provider: string;
  status: string;
  findings_json: unknown;
  architecture_summary: string | null;
  tech_debt_score: number | null;
  tokens_used: number | null;
  cost_usd: number | null;
  duration_ms: number | null;
  p0_count: number | null;
  p1_count: number | null;
  p2_count: number | null;
  review_engine?: 'manus' | 'gemini_opus';
  review_score?: number | null;
  review_depth?: 'fast' | 'deep';
}

interface Props {
  comparisons: ProviderResult[];
  /** ISO date of when the last deep review was run */
  lastDeepReviewAt?: string | null;
  /** ISO date of when the last quick review was run */
  lastQuickReviewAt?: string | null;
}

/** B5: Compute freshness label and color from a review date */
function reviewFreshness(dateStr?: string | null): { label: string; color: string } | null {
  if (!dateStr) return null;
  const daysAgo = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (daysAgo > 30) return { label: `${daysAgo}d ago — review is outdated`, color: '#ef4444' };
  if (daysAgo > 14) return { label: `${daysAgo}d ago — review may be stale`, color: '#f59e0b' };
  return { label: `${daysAgo}d ago`, color: '#6b7280' };
}

export function ReviewComparison({ comparisons, lastDeepReviewAt, lastQuickReviewAt }: Props) {
  if (comparisons.length === 0) return null;

  // Separate into deep (Manus) and quick (Gemini→Opus) tracks
  const deepReviews = comparisons.filter((c) => c.review_engine === 'manus' || c.provider === 'manus');
  const quickReviews = comparisons.filter((c) => c.review_engine !== 'manus' && c.provider !== 'manus');

  const deepFreshness = reviewFreshness(lastDeepReviewAt);
  const quickFreshness = reviewFreshness(lastQuickReviewAt);

  return (
    <div style={{ marginTop: 24 }}>
      <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--text)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
        <AlertTriangle size={14} />
        Architecture Reviews
      </h3>
      <div style={{ display: 'grid', gridTemplateColumns: deepReviews.length > 0 ? '1fr 1fr' : '1fr', gap: 12 }}>
        {/* Deep Review (Manus) track -- hidden until a deep review exists (Manus path retired, ocn-S480) */}
        <div style={{ display: deepReviews.length > 0 ? undefined : 'none', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: 'var(--orange)' }}>
            <Building2 size={16} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>Deep Review</span>
          </div>
          {deepFreshness && (
            <div style={{ fontSize: 10, marginBottom: 12, color: deepFreshness.color }}>
              <Clock size={9} style={{ display: 'inline', marginRight: 4 }} />Last deep review: {deepFreshness.label}
            </div>
          )}
          {deepReviews.length > 0 ? (
            deepReviews.map((c) => {
              const durationSec = c.duration_ms ? Math.round(c.duration_ms / 1000) : 0;
              const durationStr = durationSec > 60 ? `${Math.round(durationSec / 60)}m ${durationSec % 60}s` : `${durationSec}s`;
              return (
                <div key={c.provider + '-deep'}>
                  {c.status === 'completed' ? (
                    <>
                      {/* Manus Score — only shown for deep reviews */}
                      {c.review_score != null && (
                        <div style={{ marginBottom: 12, textAlign: 'center' }}>
                          <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--orange)' }}>{Number(c.review_score).toFixed(1)}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>Audit Score (0-5.0)</div>
                        </div>
                      )}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12, textAlign: 'center' }}>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--red)' }}>{c.p0_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P0</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--orange)' }}>{c.p1_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P1</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--blue)' }}>{c.p2_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P2</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={10} />
                          Duration: <span style={{ color: 'var(--text)' }}>{durationStr}</span>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', padding: '16px 0', textAlign: 'center' }}>
                      {c.status === 'running' ? 'In progress...' : c.status === 'failed' ? 'Failed' : 'Pending'}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', padding: '16px 0', textAlign: 'center' }}>
              No deep review yet
            </div>
          )}
        </div>

        {/* Quick Review (Gemini→Opus) track */}
        <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, color: '#a78bfa' }}>
            <Zap size={16} />
            <span style={{ fontSize: 'var(--text-sm)', fontWeight: 500 }}>Architecture review</span>
          </div>
          {quickFreshness && (
            <div style={{ fontSize: 10, marginBottom: 12, color: quickFreshness.color }}>
              <Clock size={9} style={{ display: 'inline', marginRight: 4 }} />Last quick review: {quickFreshness.label}
            </div>
          )}
          {quickReviews.length > 0 ? (
            quickReviews.map((c) => {
              const durationSec = c.duration_ms ? Math.round(c.duration_ms / 1000) : 0;
              const durationStr = durationSec > 60 ? `${Math.round(durationSec / 60)}m ${durationSec % 60}s` : `${durationSec}s`;
              const totalFindings = (c.p0_count ?? 0) + (c.p1_count ?? 0) + (c.p2_count ?? 0);
              return (
                <div key={c.provider + '-quick'}>
                  {c.status === 'completed' ? (
                    <>
                      <div style={{ marginBottom: 12, textAlign: 'center' }}>
                        <div style={{ fontSize: 24, fontWeight: 700, color: '#a78bfa' }}>{totalFindings}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>Findings</div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12, textAlign: 'center' }}>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--red)' }}>{c.p0_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P0</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--orange)' }}>{c.p1_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P1</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 'var(--text-lg)', fontWeight: 700, color: 'var(--blue)' }}>{c.p2_count ?? 0}</div>
                          <div style={{ fontSize: 10, color: 'var(--text-dim)' }}>P2</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--text-xs)', color: 'var(--text-dim)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Clock size={10} />
                          Duration: <span style={{ color: 'var(--text)' }}>{durationStr}</span>
                        </div>
                      </div>
                      {c.architecture_summary && (
                        <div style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-muted)',
                          marginTop: 12,
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}>
                          <ReactMarkdown>{c.architecture_summary}</ReactMarkdown>
                        </div>
                      )}
                    </>
                  ) : (
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', padding: '16px 0', textAlign: 'center' }}>
                      {c.status === 'running' ? 'In progress...' : c.status === 'failed' ? 'Failed' : 'Pending'}
                    </div>
                  )}
                </div>
              );
            })
          ) : (
            <div style={{ fontSize: 'var(--text-xs)', color: 'var(--text-dim)', padding: '16px 0', textAlign: 'center' }}>
              No quick review yet
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
